import React, { useEffect, useState, useRef } from 'react';
import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import IconButton from '@mui/material/IconButton';
import { Geolocation } from '@capacitor/geolocation';

const MyLocationMarker = ({ initialLocation, onLocationUpdate, motionTrackingActive }) => {
  const [currentLocation, setCurrentLocation] = useState(initialLocation);
  const watchIdRef = useRef(null);
  const map = useMap();

  // Update current location when initial location changes
  useEffect(() => {
    if (initialLocation) {
      setCurrentLocation(initialLocation);
    }
  }, [initialLocation]);

  // Start real-time location tracking
  useEffect(() => {
    let isMounted = true;

    const startLocationTracking = async () => {
      try {
        // Start watching for location updates
        watchIdRef.current = await Geolocation.watchPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 1000,
        }, (position, err) => {
          if (err || !isMounted) return;
          
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          console.log('📍 MyLocationMarker: Real-time location update:', newLocation);
          setCurrentLocation(newLocation);
          
          // Notify parent of location updates
          if (onLocationUpdate) {
            onLocationUpdate(newLocation);
          }
        });
        
        console.log('📍 MyLocationMarker: Real-time tracking started');
        
      } catch (error) {
        if (!isMounted) return;
        console.error('📍 MyLocationMarker: Tracking error:', error);
      }
    };

    // Only start tracking if we have an initial location
    if (initialLocation) {
      startLocationTracking();
    }

    return () => {
      isMounted = false;
      if (watchIdRef.current) {
        Geolocation.clearWatch({ id: watchIdRef.current });
      }
    };
  }, [initialLocation, onLocationUpdate]);

  const handleMyLocationClick = async () => {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      });
      
      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      console.log('📍 MyLocationMarker: Location button clicked:', location);
      setCurrentLocation(location);
      
      // Center map on current location
      if (map) {
        map.panTo(location);
        map.setZoom(20);
      }
      
      // Notify parent of location update
      if (onLocationUpdate) {
        onLocationUpdate(location);
      }
    } catch (error) {
      console.error('📍 MyLocationMarker: Location button error:', error);
    }
  };

  // Don't render anything if no location is available
  if (!currentLocation) {
    return null;
  }

  return (
    <>
      {/* My Location Marker */}
      {currentLocation && (
        <AdvancedMarker
          position={currentLocation}
          title="My Location"
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#4285F4',
            border: '2px solid #ffffff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
          }} />
        </AdvancedMarker>
      )}
      
      {/* My Location Button */}
      <IconButton 
        
        id="my-location-btn" 
        onClick={handleMyLocationClick} 
        aria-label="My Location" 
        size="large"
        title="Center map on my location"
      >
        <MyLocationIcon />
      </IconButton>
    </>
  );
};

export default MyLocationMarker;